import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Coffee, UtensilsCrossed, Baby, Mail, Sparkles, Utensils, Sun, Sandwich, IceCream, CupSoda } from 'lucide-react';
import { ASSETS, CONTACT } from '../components/Layout';

const MenuPage = () => {
  const [activeTab, setActiveTab] = useState('main-menu');

  // Section Header Component
  const SectionHeader = ({ icon: Icon, title, subtitle }) => (
    <div className="text-center mb-12">
      <Icon className="w-12 h-12 text-sage mx-auto mb-4" />
      <h2 className="font-serif text-4xl md:text-5xl text-walnut mb-3">{title}</h2>
      {subtitle && <p className="text-walnut/70 text-lg">{subtitle}</p>}
    </div>
  );

  // Menu Item Component
  const MenuItem = ({ name, price, description, highlight }) => (
    <div className={`py-3 ${highlight ? 'bg-sage/10 px-4 rounded-lg -mx-4' : ''}`}>
      <div className="flex items-baseline justify-between gap-4">
        <span className="font-serif text-lg text-walnut font-medium">{name}</span>
        <span className="font-sans text-walnut font-semibold whitespace-nowrap">{price}</span>
      </div>
      {description && (
        <p className="text-walnut/70 text-sm mt-1">{description}</p>
      )}
    </div>
  );

  // Category Header
  const CategoryHeader = ({ title, subtitle }) => (
    <div className="mb-6 pb-3 border-b-2 border-sage">
      <h3 className="font-serif text-2xl text-walnut">{title}</h3>
      {subtitle && <p className="text-walnut/60 text-sm mt-1">{subtitle}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-cream" data-testid="menu-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={ASSETS.brick}
            alt="Bean in Stony interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-cream" />
        </div>
        
        <div className="relative z-10 text-center px-6">
          <p className="font-accent text-white text-2xl mb-4 drop-shadow-lg">Homemade with Love</p>
          <h1 
            className="font-serif text-5xl md:text-7xl text-white font-bold drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]"
            data-testid="menu-headline"
          >
            Our Menu
          </h1>
        </div>
      </section>

      {/* Menu Tabs Section */}
      <section className="section-padding" data-testid="menu-content-section">
        <div className="max-w-5xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Tab Navigation */}
            <TabsList className="w-full flex flex-wrap justify-center gap-2 bg-transparent mb-12 h-auto" data-testid="menu-tabs">
              <TabsTrigger 
                value="main-menu"
                className="px-6 py-3 font-sans font-medium text-walnut data-[state=active]:bg-walnut data-[state=active]:text-cream rounded-full border-2 border-walnut hover:bg-walnut/10 transition-all"
                data-testid="tab-main-menu"
              >
                <Utensils className="w-4 h-4 mr-2" />
                Main Menu
              </TabsTrigger>
              <TabsTrigger 
                value="afternoon-tea"
                className="px-6 py-3 font-sans font-medium text-walnut data-[state=active]:bg-walnut data-[state=active]:text-cream rounded-full border-2 border-walnut hover:bg-walnut/10 transition-all"
                data-testid="tab-afternoon-tea"
              >
                <Coffee className="w-4 h-4 mr-2" />
                Afternoon Tea
              </TabsTrigger>
              <TabsTrigger 
                value="buffet"
                className="px-6 py-3 font-sans font-medium text-walnut data-[state=active]:bg-walnut data-[state=active]:text-cream rounded-full border-2 border-walnut hover:bg-walnut/10 transition-all"
                data-testid="tab-buffet"
              >
                <UtensilsCrossed className="w-4 h-4 mr-2" />
                Buffet Menu
              </TabsTrigger>
              <TabsTrigger 
                value="kids"
                className="px-6 py-3 font-sans font-medium text-walnut data-[state=active]:bg-walnut data-[state=active]:text-cream rounded-full border-2 border-walnut hover:bg-walnut/10 transition-all"
                data-testid="tab-kids"
              >
                <Baby className="w-4 h-4 mr-2" />
                Little Monsters
              </TabsTrigger>
            </TabsList>

            {/* ==================== MAIN MENU ==================== */}
            <TabsContent value="main-menu" className="mt-0" data-testid="content-main-menu">
              <div className="bg-white/60 backdrop-blur-sm p-8 md:p-12 rounded-lg shadow-heritage border border-stone-light">
                <SectionHeader 
                  icon={Utensils}
                  title="Main Menu"
                  subtitle="Fresh, homemade dishes served daily"
                />

                <div className="space-y-16">
                  
                  {/* ===== BREAKFAST ===== */}
                  <div>
                    <CategoryHeader title="Breakfast" subtitle="Served 8:00am - 11:00am" />
                    
                    {/* Early Bird Special */}
                    <div className="bg-gold/10 p-4 rounded-lg border border-gold/30 mb-6">
                      <div className="flex items-baseline justify-between">
                        <span className="font-serif text-xl text-walnut font-semibold">Early Bird Special</span>
                        <span className="font-sans text-walnut font-bold text-xl">£9.50</span>
                      </div>
                      <p className="text-walnut/70 text-sm mt-1">6 Items including coffee/tea refills (8am-11am)</p>
                    </div>

                    <div className="space-y-2">
                      <MenuItem name="All Day Breakfast" price="£8.90 - £12.30" description="4 items (£8.90) | 6 items (£10.00) | 8 items (£11.10) | 10 items (£12.30)" />
                      <MenuItem name="Breakfast Baguette/Wrap" price="£7.50" description="4 items" />
                      <MenuItem name="Hot Sandwich/Bap" price="£7.30" description="3 items" />
                      <MenuItem name="Breakfast Sides/Extras" price="£2.00" description="Per item" />
                    </div>

                    <div className="mt-4 p-4 bg-cream-hover/50 rounded-lg">
                      <p className="text-sm text-walnut/80 font-medium mb-2">Extra items - Choose from:</p>
                      <p className="text-walnut/70 text-sm">
                        Bacon • Sausage • Egg (Fried, Poached or Scrambled) • Beans • Fresh or Tinned Tomatoes • 
                        Mushrooms • Black Pudding • Hash Browns • Fried Bread • Vegetarian Sausage
                      </p>
                    </div>

                    <div className="mt-4 space-y-2">
                      <MenuItem name="Homemade Porridge" price="£4.70" description="Served 8:00am-11:30am. Add toppings for £1.50" />
                      <MenuItem name="A Slice of Toast" price="£0.80" />
                    </div>
                  </div>

                  {/* ===== SIGNATURE EGG DISHES ===== */}
                  <div>
                    <CategoryHeader title="Signature Egg Dishes" />
                    <div className="space-y-2">
                      <MenuItem name="Eggs Royale" price="£11.00" description="With Salmon" />
                      <MenuItem name="Eggs Benedict" price="£9.90" description="With Bacon" />
                      <MenuItem name="Eggs Florentine" price="£9.60" description="With Spinach" />
                      <MenuItem name="Avocado Benedict" price="£9.60" />
                      <MenuItem name="Poached Egg, Bacon & Avocado on Sourdough" price="£10.50" />
                      <MenuItem name="Smoked Salmon & Scrambled Egg on Granary" price="£10.20" />
                      <MenuItem name="Salmon & Cream Cheese Bagel" price="£9.50" />
                      <MenuItem name="2 Item Toast" price="£6.50" />
                    </div>
                  </div>

                  {/* ===== LUNCH ===== */}
                  <div>
                    <CategoryHeader title="Lunch" />
                    
                    {/* Sandwiches */}
                    <div className="mb-8">
                      <h4 className="font-serif text-xl text-walnut font-semibold mb-3">Sandwiches / Baps / Baguettes</h4>
                      <p className="text-walnut/70 text-sm mb-2">Starting from £6.20 (Standard) to £7.00 (Premium fillings)</p>
                      <p className="text-walnut/60 text-sm italic">
                        Premium fillings: Brie • Bacon • Ham • Smoked Salmon • Chicken • Coronation Chicken • Sweet Chilli Chicken • Prawn
                      </p>
                    </div>

                    {/* Toasties */}
                    <div className="mb-8">
                      <h4 className="font-serif text-xl text-walnut font-semibold mb-3">Toasties, Paninis & Toasted Wraps</h4>
                      <p className="text-walnut/70 text-sm mb-2">Served with salad or chips — £8.50 - £8.70</p>
                      <p className="text-walnut/60 text-sm">
                        Cheese & Ham • Mozzarella & Pesto • Tuna Melt with Onion • Brie & Cranberry • 
                        BBQ Chicken & Cheese • Mozzarella & Sun-Dried Tomatoes • Cheddar & Caramelised Onion • 
                        Brie & Bacon • Sweet Chilli Chicken & Cheese • Chicken, Spinach, Mozzarella, Tomato & Onion
                      </p>
                    </div>

                    <div className="space-y-2 mb-8">
                      <MenuItem name="Jacket Potato" price="£9.50" description="With salad and coleslaw (includes 1 filling). Choose: Cheese • Tuna • Beans • Prawn • Cream Cheese & Bacon • Coronation Chicken. Extra filling: £2.00" />
                      <MenuItem name="Omelette (2 fillings)" price="£10.50" description="Served with salad or chips" />
                      <MenuItem name="Ham, Egg & Chips" price="£9.90" />
                      <MenuItem name="Soup of the Day" price="£7.30" description="With baguette or bread" />
                    </div>

                    {/* Specialty Items */}
                    <div className="bg-walnut/5 p-6 rounded-lg mb-6">
                      <h4 className="font-serif text-xl text-walnut font-semibold mb-4">Specialty Dishes</h4>
                      <div className="space-y-3">
                        <MenuItem name="Double Bacon & Cheeseburger" price="£18.50" description="Two 6oz steak burgers, served with chips and onion rings" />
                        <MenuItem name="6oz Steak, Bacon & Cheeseburger" price="£12.20" description="6oz steak burger with bacon, served with chips and onion rings" />
                        <MenuItem name="Chicken Stacker Burger" price="£11.70" description="Three chicken goujons served with salad, chips, onion rings & spicy mayo" />
                        <MenuItem name="Club Sandwich" price="£10.50" description="Grilled chicken, bacon, lettuce, mayo and cheese with chips" />
                        <MenuItem name="Crispy Chicken & Bacon Wrap" price="£7.50" description="Chicken goujons, crispy chopped bacon, spicy mayo & lettuce" />
                        <MenuItem name="Coronation Chilli Chicken Bagel" price="£7.20" description="Coronation chicken, chilli jam & rocket on a toasted bagel" />
                        <MenuItem name="Croque-Monsieur Toastie" price="£7.80" description="Ham, cheese & dijon mustard" />
                        <MenuItem name="The Puccini" price="£8.50" description="Salami, Parma ham, gruyere cheese, rocket, sun dried tomato and pesto in a toasted bloomer or ciabatta" />
                        <MenuItem name="Fillet Steak Ciabatta" price="£10.90" description="Thinly sliced steak, horseradish mayo & rocket" />
                        <MenuItem name="New Yorker Toastie" price="£8.50" description="Pastrami, gruyere cheese, pickles, dijon mustard & mayo" />
                      </div>
                    </div>
                  </div>

                  {/* ===== SIDES ===== */}
                  <div>
                    <CategoryHeader title="Sides" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                      <MenuItem name="Chips" price="£3.80" />
                      <MenuItem name="Cheesy Chips" price="£4.50" />
                      <MenuItem name="Cheesy Chips with Bacon" price="£5.00" />
                      <MenuItem name="Fried Halloumi" price="£5.00" />
                      <MenuItem name="Coleslaw" price="£2.50" />
                    </div>
                  </div>

                  {/* ===== CAKES ===== */}
                  <div>
                    <CategoryHeader title="Cakes" subtitle="Selection of Homemade" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                      <MenuItem name="Cakes & Traybakes" price="£4.20" />
                      <MenuItem name="Toasted Tea Cake" price="£3.60" />
                      <MenuItem name="Croissant" price="£3.50" description="With jam & butter" />
                      <MenuItem name="Scone" price="£4.20" description="With jam & butter" />
                      <MenuItem name="Cream Tea for One" price="£7.90" description="Pot of tea, one scone with clotted cream" />
                    </div>
                  </div>

                  {/* ===== HOT DRINKS ===== */}
                  <div>
                    <CategoryHeader title="Hot Drinks" />
                    
                    {/* Coffee */}
                    <div className="mb-6">
                      <h4 className="font-serif text-lg text-walnut font-semibold mb-3">Coffee</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-stone-light">
                              <th className="text-left py-2 font-medium text-walnut/70"></th>
                              <th className="text-right py-2 font-medium text-walnut/70 px-2">Regular</th>
                              <th className="text-right py-2 font-medium text-walnut/70">Large</th>
                            </tr>
                          </thead>
                          <tbody className="text-walnut">
                            <tr className="border-b border-stone-light/50">
                              <td className="py-2 font-serif">Coffee</td>
                              <td className="text-right px-2">£3.50</td>
                              <td className="text-right">£3.70</td>
                            </tr>
                            <tr className="border-b border-stone-light/50">
                              <td className="py-2 font-serif">Cortado</td>
                              <td className="text-right px-2">£3.30</td>
                              <td className="text-right">-</td>
                            </tr>
                            <tr className="border-b border-stone-light/50">
                              <td className="py-2 font-serif">Cappuccino</td>
                              <td className="text-right px-2">£3.80</td>
                              <td className="text-right">£4.00</td>
                            </tr>
                            <tr className="border-b border-stone-light/50">
                              <td className="py-2 font-serif">Latte</td>
                              <td className="text-right px-2">£3.80</td>
                              <td className="text-right">£4.00</td>
                            </tr>
                            <tr className="border-b border-stone-light/50">
                              <td className="py-2 font-serif">Flat White</td>
                              <td className="text-right px-2">£3.30</td>
                              <td className="text-right">-</td>
                            </tr>
                            <tr className="border-b border-stone-light/50">
                              <td className="py-2 font-serif">Mocha</td>
                              <td className="text-right px-2">£4.10</td>
                              <td className="text-right">£4.30</td>
                            </tr>
                            <tr className="border-b border-stone-light/50">
                              <td className="py-2 font-serif">Espresso</td>
                              <td className="text-right px-2">£2.50</td>
                              <td className="text-right">£3.00</td>
                            </tr>
                            <tr className="border-b border-stone-light/50">
                              <td className="py-2 font-serif">Hot Chocolate</td>
                              <td className="text-right px-2" colSpan="2">£3.90</td>
                            </tr>
                            <tr className="border-b border-stone-light/50">
                              <td className="py-2 font-serif">Luxury Hot Chocolate</td>
                              <td className="text-right px-2" colSpan="2">£4.60</td>
                            </tr>
                            <tr>
                              <td className="py-2 font-serif">Baby Chino</td>
                              <td className="text-right px-2" colSpan="2">£2.10</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p className="text-walnut/60 text-xs mt-2 italic">Luxury Hot Chocolate served with cream and marshmallows</p>
                    </div>

                    {/* Tea */}
                    <div>
                      <h4 className="font-serif text-lg text-walnut font-semibold mb-3">Tea</h4>
                      <div className="space-y-2">
                        <MenuItem name="Pot of Tea for One" price="£3.10" />
                        <MenuItem name="Pot of Tea for Two" price="£4.70" />
                        <MenuItem name="Mug of Tea" price="£2.90" />
                        <MenuItem name="Speciality Tea" price="£3.30" description="Green • Earl Grey • Peppermint • Red Berry • Lemon & Ginger • Camomile • Red Bush" />
                      </div>
                      <p className="text-walnut/60 text-xs mt-2 italic">Decaf available</p>
                    </div>
                  </div>

                  {/* ===== COLD DRINKS ===== */}
                  <div>
                    <CategoryHeader title="Cold Drinks" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                      <MenuItem name="Sanpellegrino" price="£2.50" />
                      <MenuItem name="Freshly Squeezed Orange Juice" price="£4.50" />
                      <MenuItem name="Apple Juice" price="£2.80" />
                      <MenuItem name="Bottled Water" price="£1.80" />
                      <MenuItem name="Sparkling Water" price="£1.80" />
                      <MenuItem name="Fruit Shoots" price="£1.80" />
                      <MenuItem name="Canned Drinks" price="£1.90" />
                      <MenuItem name="Glass of Milk" price="£1.90" />
                      <MenuItem name="Frappuccino" price="£5.50" />
                      <MenuItem name="Iced Coffee" price="£5.50" />
                    </div>

                    <div className="mt-6 space-y-4">
                      <div>
                        <MenuItem name="Smoothies" price="£5.50" />
                        <p className="text-walnut/70 text-sm ml-4">
                          All Yellow: Mango, Banana & Pineapple<br />
                          Very Berry: Strawberry, Raspberry & Blueberry
                        </p>
                      </div>
                      <div>
                        <MenuItem name="Milkshakes" price="£5.50" />
                        <p className="text-walnut/70 text-sm ml-4">
                          Chocolate • Banana • Strawberry • Vanilla • Toffee • Raspberry • Caramel
                        </p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </TabsContent>

            {/* ==================== AFTERNOON TEA ==================== */}
            <TabsContent value="afternoon-tea" className="mt-0" data-testid="content-afternoon-tea">
              <div className="bg-white/60 backdrop-blur-sm p-8 md:p-12 rounded-lg shadow-heritage border border-stone-light">
                <SectionHeader 
                  icon={Coffee}
                  title="Afternoon Tea at Bean in Stony"
                  subtitle="Perfect for groups and special occasions"
                />

                <div className="max-w-2xl mx-auto">
                  {/* Price */}
                  <div className="text-center mb-10 p-6 bg-sage/10 rounded-lg border border-sage/30">
                    <p className="font-serif text-5xl text-walnut font-bold">£25</p>
                    <p className="text-walnut/70 mt-2">per person (Minimum of 4 people)</p>
                  </div>

                  {/* Includes */}
                  <div className="space-y-8">
                    <div>
                      <h3 className="font-serif text-2xl text-walnut mb-4 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-sage" />
                        Includes
                      </h3>
                      <div className="space-y-2 pl-7">
                        <div className="flex items-start gap-3">
                          <span className="w-2 h-2 bg-sage rounded-full mt-2 flex-shrink-0" />
                          <span className="text-walnut/80">Selection of Sandwiches & Homemade Savouries</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="w-2 h-2 bg-sage rounded-full mt-2 flex-shrink-0" />
                          <span className="text-walnut/80">Homemade Cream Cakes & Selection of Homemade Cakes</span>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="w-2 h-2 bg-sage rounded-full mt-2 flex-shrink-0" />
                          <span className="text-walnut/80">Homemade Scones with Clotted Cream and Jam</span>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-stone-light pt-6">
                      <h3 className="font-serif text-2xl text-walnut mb-4">Drinks</h3>
                      <p className="text-walnut/80">
                        Includes refillable hot or cold drinks of your choice.
                      </p>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-10 text-center">
                    <a
                      href={`mailto:${CONTACT.email}?subject=Afternoon%20Tea%20Enquiry%20–%20Bean%20in%20Stony`}
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid="afternoon-tea-cta"
                    >
                      <Mail className="w-5 h-5" />
                      Enquire About Afternoon Tea
                    </a>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ==================== BUFFET MENU ==================== */}
            <TabsContent value="buffet" className="mt-0" data-testid="content-buffet">
              <div className="bg-white/60 backdrop-blur-sm p-8 md:p-12 rounded-lg shadow-heritage border border-stone-light">
                <SectionHeader 
                  icon={UtensilsCrossed}
                  title="Buffet Menu"
                  subtitle="Customizable catering for your event"
                />

                <div className="max-w-3xl mx-auto space-y-12">
                  
                  {/* Section 1: Sandwich and Wrap Platters */}
                  <div>
                    <h3 className="font-serif text-3xl text-walnut mb-6 pb-3 border-b-2 border-sage text-center">
                      Sandwich & Wrap Platters
                    </h3>
                    
                    {/* Pricing Options */}
                    <div className="grid md:grid-cols-2 gap-6 mb-8">
                      <div className="p-6 bg-sage/10 rounded-lg border border-sage/30 text-center">
                        <p className="font-serif text-3xl text-walnut font-bold">£10.80</p>
                        <p className="text-walnut/70 mt-2 text-sm">
                          Selection of Sandwiches or Wraps<br />with Four Sides of Your Choice
                        </p>
                      </div>
                      <div className="p-6 bg-walnut rounded-lg text-center">
                        <p className="font-serif text-3xl text-cream font-bold">£13.20</p>
                        <p className="text-cream mt-2 text-sm">
                          Selection of Sandwiches or Wraps<br />with Six Sides of Your Choice
                        </p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h4 className="font-serif text-xl text-walnut mb-3 font-semibold">
                          Selection of Canapés
                        </h4>
                        <p className="text-walnut/80 leading-relaxed">
                          Brie and Cranberry • Tuna and Cucumber • Salmon and Cream Cheese
                        </p>
                      </div>

                      <div>
                        <h4 className="font-serif text-xl text-walnut mb-3 font-semibold">
                          Turnovers
                        </h4>
                        <p className="text-walnut/80 leading-relaxed">
                          Smoked Cheese and Bacon • Brie Cranberry and Bacon
                        </p>
                      </div>

                      <div>
                        <h4 className="font-serif text-xl text-walnut mb-3 font-semibold">
                          Other Buffet Sides
                        </h4>
                        <p className="text-walnut/80 leading-relaxed">
                          Chicken Goujons • Breaded Prawns • Mini Pizza • Vegetarian Samosas • 
                          Vegetarian Spring Rolls • Scotch Eggs • Pork Pie • BBQ Chicken Wings • 
                          Duck and Hoisin Wraps • Mozzarella Sticks • Sausage Rolls • Vol-au-vents • 
                          Mini Filled Croissants • Cheese Twists • Bruschetta • Homemade Quiche • 
                          Salmon Mousse Blinis • Chicken Tikka Skewers
                        </p>
                      </div>

                      <div>
                        <h4 className="font-serif text-xl text-walnut mb-3 font-semibold">
                          Selection of Fruit
                        </h4>
                        <p className="text-walnut/80 leading-relaxed">
                          Fresh seasonal fruit selection
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Specialty Platters */}
                  <div>
                    <h3 className="font-serif text-3xl text-walnut mb-6 pb-3 border-b-2 border-sage text-center">
                      Specialty Platters
                    </h3>
                    
                    <div className="space-y-6">
                      <div className="p-6 bg-cream-hover/50 rounded-lg border border-stone-light">
                        <div className="flex items-baseline justify-between mb-3">
                          <h4 className="font-serif text-xl text-walnut font-semibold">Ploughman's</h4>
                          <span className="font-sans text-walnut font-bold text-lg">£12.50ph</span>
                        </div>
                        <p className="text-walnut/80 text-sm leading-relaxed">
                          Ham and Turkey, Cheddar and Brie, Potato or Pasta Salad, Salad with Coleslaw, 
                          Cous Cous, Pork Pies, Crusty Bread, and Dressings
                        </p>
                      </div>

                      <div className="p-6 bg-cream-hover/50 rounded-lg border border-stone-light">
                        <div className="flex items-baseline justify-between mb-3">
                          <h4 className="font-serif text-xl text-walnut font-semibold">Three Meat Ploughman's with Salmon</h4>
                          <span className="font-sans text-walnut font-bold text-lg">£13.60ph</span>
                        </div>
                        <p className="text-walnut/80 text-sm leading-relaxed">
                          Ham, Beef and Turkey, Cheddar and Brie, Salmon, Potato or Pasta Salad, 
                          Salad with Coleslaw, Cous Cous, Pork Pies, Crusty Bread, and Dressings
                        </p>
                      </div>

                      <div className="p-6 bg-walnut/10 rounded-lg border border-walnut/20">
                        <div className="flex items-baseline justify-between mb-3">
                          <h4 className="font-serif text-xl text-walnut font-semibold">Mediterranean Mezze</h4>
                          <span className="font-sans text-walnut font-bold text-lg">£14.40ph</span>
                        </div>
                        <p className="text-walnut/80 text-sm leading-relaxed">
                          Salami, Chorizo, Prosciutto, Smoked Cheese, Cheddar Cheese, Feta, Olives, 
                          Sun Dried Tomatoes, Oil and Balsamic Dressing, Pitta Bread, Selection of Bread, 
                          Houmous, Guacamole, Salad, and Cous Cous
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Breakfast and Light Options */}
                  <div>
                    <h3 className="font-serif text-3xl text-walnut mb-6 pb-3 border-b-2 border-sage text-center">
                      Breakfast & Light Options
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-baseline justify-between py-3 border-b border-stone-light">
                        <div>
                          <span className="font-serif text-lg text-walnut font-medium">Breakfast</span>
                          <p className="text-walnut/70 text-sm">Selection of Pastries</p>
                        </div>
                        <span className="font-sans text-walnut font-semibold">£5.80ph</span>
                      </div>

                      <div className="flex items-baseline justify-between py-3 border-b border-stone-light">
                        <div>
                          <span className="font-serif text-lg text-walnut font-medium">Breakfast with Fruit Scones</span>
                          <p className="text-walnut/70 text-sm">Selection of Pastries, Fruit Scones, Clotted Cream and Jam</p>
                        </div>
                        <span className="font-sans text-walnut font-semibold">£8.20ph</span>
                      </div>

                      <div className="flex items-baseline justify-between py-3 border-b border-stone-light">
                        <div>
                          <span className="font-serif text-lg text-walnut font-medium">Fruit Platter</span>
                          <p className="text-walnut/70 text-sm">Selection of Fruit</p>
                        </div>
                        <span className="font-sans text-walnut font-semibold">£1.80ph</span>
                      </div>

                      <div className="flex items-baseline justify-between py-3 border-b border-stone-light">
                        <div>
                          <span className="font-serif text-lg text-walnut font-medium">Cake Platter</span>
                          <p className="text-walnut/70 text-sm">Selection of Homemade Cake</p>
                        </div>
                        <span className="font-sans text-walnut font-semibold">£3.70ph</span>
                      </div>

                      <div className="flex items-baseline justify-between py-3 border-b border-stone-light">
                        <div>
                          <span className="font-serif text-lg text-walnut font-medium">Salad Platter with a Filling</span>
                          <p className="text-walnut/70 text-sm">Choice of Chicken (Coronation, Mayo), Prawn, or Tuna</p>
                        </div>
                        <span className="font-sans text-walnut font-semibold">£7.20ph</span>
                      </div>

                      <div className="flex items-baseline justify-between py-3 border-b border-stone-light">
                        <div>
                          <span className="font-serif text-lg text-walnut font-medium">Homemade Soup and Bread</span>
                          <p className="text-walnut/70 text-sm">
                            Served with a selection of breads. Choose from Chicken and Veg, Pea and Ham, 
                            Leek and Potato, Spicy Butternut and Red Pepper, Roasted Tomato and Garlic, 
                            or Cream of Mushroom
                          </p>
                        </div>
                        <span className="font-sans text-walnut font-semibold">£7.20ph</span>
                      </div>

                      <div className="flex items-baseline justify-between py-3 border-b border-stone-light">
                        <div>
                          <span className="font-serif text-lg text-walnut font-medium">Sandwich and Wraps</span>
                          <p className="text-walnut/70 text-sm">Selection of Sandwiches and Wraps</p>
                        </div>
                        <span className="font-sans text-walnut font-semibold">£6.00</span>
                      </div>
                    </div>
                  </div>

                  {/* Buffet CTA */}
                  <div className="mt-12 p-6 bg-gold/10 rounded-lg border border-gold/30 text-center">
                    <p className="font-serif text-xl text-walnut mb-4">
                      Planning an event? Let us help make it special.
                    </p>
                    <a
                      href="mailto:Thebeaninstonybuffet@gmail.com?subject=Buffet%20%26%20Event%20Inquiry%20–%20Bean%20in%20Stony"
                      className="btn-primary inline-flex items-center gap-2 bg-gold hover:bg-copper"
                      data-testid="buffet-enquire-cta"
                    >
                      <Mail className="w-5 h-5" />
                      Enquire for Your Event
                    </a>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ==================== KIDS MENU ==================== */}
            <TabsContent value="kids" className="mt-0" data-testid="content-kids">
              <div className="bg-white/60 backdrop-blur-sm p-8 md:p-12 rounded-lg shadow-heritage border border-stone-light">
                <SectionHeader 
                  icon={Baby}
                  title="Little Monsters Menu"
                  subtitle="Delicious meals for our younger guests"
                />

                <div className="max-w-2xl mx-auto space-y-10">
                  {/* Breakfast */}
                  <div>
                    <h3 className="font-serif text-2xl text-walnut mb-6 pb-2 border-b-2 border-sage">
                      Breakfast
                    </h3>
                    <div className="space-y-1">
                      <MenuItem name="3-Item Breakfast" price="£5.50" />
                      <MenuItem name="1 Item on Toast" price="£3.50" />
                      <MenuItem name="Homemade Porridge" price="£3.10" description="Extra items £1.00" />
                    </div>
                  </div>

                  {/* Lunch Box */}
                  <div>
                    <h3 className="font-serif text-2xl text-walnut mb-6 pb-2 border-b-2 border-sage">
                      Lunch Box
                    </h3>
                    <div className="p-6 bg-sage/10 rounded-lg border border-sage/30">
                      <div className="flex items-baseline justify-between mb-3">
                        <span className="font-serif text-xl text-walnut font-medium">Lunch Box</span>
                        <span className="font-sans text-walnut font-semibold">£5.50</span>
                      </div>
                      <p className="text-walnut/70 text-sm">
                        Includes: Sandwich (Tuna, Cheese, Jam, Ham, or Nutella), 
                        Fruit Shoot, Crisps, Biscuit, and Fruit.
                      </p>
                    </div>
                  </div>

                  {/* Mains */}
                  <div>
                    <h3 className="font-serif text-2xl text-walnut mb-2 pb-2 border-b-2 border-sage">
                      Mains
                    </h3>
                    <p className="text-walnut/70 text-sm mb-6 italic">
                      All mains include a Fruit Shoot
                    </p>
                    <div className="space-y-1">
                      <MenuItem name="Cheese or Ham Omelette & Beans" price="£6.50" />
                      <MenuItem name="Sausage, Chips & Beans" price="£6.50" />
                      <MenuItem name="Mini Pizza, Chips & Beans" price="£6.50" />
                      <MenuItem name="Chicken Burger, Chips & Beans" price="£6.50" />
                      <MenuItem name="Chicken Goujons, Chips & Beans" price="£6.50" />
                      <MenuItem name="Soup with a slice of bread" price="£6.50" />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Note Section */}
      <section className="section-padding bg-walnut" data-testid="menu-note-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-accent text-sage text-2xl mb-4">
            "All food baked on site and AMAZING!"
          </p>
          <p className="text-cream/80">
            All our cakes, scones, and treats are freshly made in-house daily. 
            Please let us know of any allergies or dietary requirements.
          </p>
        </div>
      </section>
    </div>
  );
};

export default MenuPage;
